import React from "react";
import { ExternalLink, FileText } from "lucide-react";
import { useSourceReader } from "./SourceReader";

const FUNDING_LABELS = {
    independent: { text: "Independent", className: "bg-[#2F80ED]/10 text-[#56CCF2] border-[#2F80ED]/30" },
    industry: { text: "Industry-funded", className: "bg-[#243044] text-[#94A3B8] border-[#243044]" },
    government: { text: "Government", className: "bg-[#56CCF2]/10 text-[#56CCF2] border-[#56CCF2]/30" },
    unknown: { text: "Funding unknown", className: "bg-[#0B1220] text-[#64748B] border-[#243044]" },
};

export default function SourceItem({ source, index }) {
    const { openSource } = useSourceReader();
    if (!source) return null;
    const funding = FUNDING_LABELS[source.funding || "unknown"] || FUNDING_LABELS.unknown;
    const open = (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; // honour new-tab
        e.preventDefault();
        openSource(source);
    };
    return (
        <li
            data-testid={`source-item-${index}`}
            id={`source-${(index ?? 0) + 1}`}
            className="bg-[#111827] border border-[#243044] rounded-xl p-5 hover:border-[#2F80ED]/40 transition-colors scroll-mt-24"
        >
            <div className="flex items-start gap-4">
                <div className="text-xs font-bold text-[#56CCF2] mt-0.5 min-w-[24px]">
                    [{(index ?? 0) + 1}]
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                        <p className="text-sm font-semibold text-white leading-snug">
                            {source.title || "Untitled source"}
                        </p>
                        <span
                            data-testid="source-bias-label"
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-medium border ${funding.className}`}
                        >
                            {funding.text}
                        </span>
                    </div>

                    <div className="text-xs text-[#94A3B8] flex flex-wrap gap-x-3 gap-y-1 mb-2">
                        {source.publisher && <span>{source.publisher}</span>}
                        {source.source_type && <span className="text-[#64748B]">· {source.source_type}</span>}
                        {source.date && <span className="text-[#64748B]">· {source.date}</span>}
                    </div>

                    {source.notes && (
                        <p className="text-sm text-[#94A3B8] leading-relaxed mb-3">{source.notes}</p>
                    )}

                    <div className="flex items-center gap-4">
                        {source.url && (
                            <a
                                href={source.url}
                                target="_blank"
                                rel="noreferrer"
                                onClick={open}
                                data-testid={`source-view-${index}`}
                                className="inline-flex items-center gap-1.5 text-xs text-[#56CCF2] hover:text-white transition-colors"
                            >
                                <ExternalLink className="w-3 h-3" />
                                View source
                            </a>
                        )}
                        {source.document_url && (
                            <a
                                href={source.document_url}
                                target="_blank"
                                rel="noreferrer"
                                onClick={open}
                                data-testid={`source-doc-${index}`}
                                className="inline-flex items-center gap-1.5 text-xs text-[#56CCF2] hover:text-white transition-colors"
                            >
                                <FileText className="w-3 h-3" />
                                Document
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </li>
    );
}
